/**
 * Normalize a mixed address object (e.g., Nominatim/OpenStreetMap or custom)
 * to your API schema with `locality` + `locality_type`.
 *
 * Usage:
 *   const apiAddress = normalizeAddress(source, { mode: 'approximate' })
 */

const DEFAULT_DROP_IN_APPROXIMATE = [
  'road',
  'building',
  'house_number',
  'apartment_number',
  // add more if you consider them too precise:
  // 'entrance', 'staircase', 'unit', 'level', 'door', 'poi'
]

const DEFAULT_KEEP_OPTIONAL = [
  'unit_id',
  'postcode',
  'country',
  'country_code',
  'state',
  'state_district',
  'province',
  'county',
  'municipality',
  'district',
  'city_district',
  'borough',
  'suburb',
  'neighbourhood',
  'quarter',
  'man_made', // keep if you treat landmarks as safe in approximate mode
  'address_comment',
  'address_confirmed',
  'ISO3166-2-lvl6',
  'ISO3166-2-lvl4',
]

/**
 * Pick the first present locality from common keys and return { value, type }.
 * Priority order is intentional and maps to your API's `locality_type`.
 */
function deriveLocality(input = {}) {
  const candidates = [
    ['city', 'city'],
    ['town', 'town'],
    ['village', 'village'],
    ['hamlet', 'hamlet'],
    ['municipality', 'municipality'],
    ['suburb', 'suburb'],
    // You can map borough/district/quarter to a type if you want,
    // but typically they sit alongside locality, not as it.
  ]

  for (const [key, type] of candidates) {
    const val = cleanStr(input[key])
    if (val) return { value: val, type }
  }

  // Fallbacks: try generic fields people sometimes send
  const fallback = cleanStr(input.locality || input.city || input.town)
  if (fallback) {
    // If caller supplied locality without a type, default to 'city'
    return { value: fallback, type: cleanStr(input.locality_type) || 'city' }
  }

  return { value: undefined, type: undefined }
}

function cleanStr(v) {
  if (v == null) return undefined
  const s = String(v).trim()
  return s.length ? s : undefined
}

function lower2(v) {
  const s = cleanStr(v)
  return s ? s.toLowerCase() : undefined
}

function stripEmpty(obj) {
  const out = {}
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v == null) return
    if (typeof v === 'string' && !v.trim()) return
    out[k] = v
  })
  return out
}

/**
 * @param {Object} input   Raw address (Nominatim-ish or custom)
 * @param {Object} options
 * @param {'exact'|'approximate'} [options.mode='exact']
 * @param {string[]} [options.dropInApproximate]  Extra keys to drop when mode='approximate'
 * @param {string[]} [options.keepOptional]       Optional keys to preserve
 * @returns {Object} API-shaped address
 */
export function normalizeAddress(input = {}, options = {}) {
  const mode = options.mode === 'approximate' ? 'approximate' : 'exact'
  const dropInApproximate = options.dropInApproximate || DEFAULT_DROP_IN_APPROXIMATE
  const keepOptional = options.keepOptional || DEFAULT_KEEP_OPTIONAL

  const { value: locality, type: locality_type } = deriveLocality(input)

  // Base API shape
  let out = {
    country: cleanStr(input.country),
    country_code: lower2(input.country_code),
    state: cleanStr(input.state) || cleanStr(input.region) || cleanStr(input.state_name),
    state_district: cleanStr(input.state_district),
    province: cleanStr(input.province),
    county: cleanStr(input.county),
    municipality: cleanStr(input.municipality),
    district: cleanStr(input.district),
    city_district: cleanStr(input.city_district),
    borough: cleanStr(input.borough),
    suburb: cleanStr(input.suburb),
    neighbourhood: cleanStr(input.neighbourhood) || cleanStr(input.neighborhood), // US/UK spelling
    quarter: cleanStr(input.quarter),
    man_made: cleanStr(input.man_made),
    unit_id: cleanStr(input.unit_id),
    locality,
    locality_type: locality_type || cleanStr(input.locality_type),

    postcode: cleanStr(input.postcode),

    // Exact-only fields (may be removed below in approximate mode)
    road: cleanStr(input.road),
    building: cleanStr(input.building),
    house_number: cleanStr(input.house_number),
    apartment_number: cleanStr(input.apartment_number),

    // Comments / flags if you use them
    address_comment: cleanStr(input.address_comment || input.comment),
    address_confirmed:
      typeof input.address_confirmed === 'boolean'
        ? input.address_confirmed
        : undefined,

    // ISO codes if present (kept only if non-empty)
    'ISO3166-2-lvl6': cleanStr(input['ISO3166-2-lvl6']),
    'ISO3166-2-lvl4': cleanStr(input['ISO3166-2-lvl4']),
  }

  // In approximate mode, drop precise fields
  if (mode === 'approximate') {
    for (const key of dropInApproximate) {
      delete out[key]
    }
  }

  // Ensure we only keep known optional keys (already part of out).
  // (This is mainly here to make it obvious what's intended; out is already curated.)
  const allowed = new Set([
    'unit_id',
    'country',
    'country_code',
    'state',
    'state_district',
    'province',
    'county',
    'municipality',
    'district',
    'city_district',
    'borough',
    'suburb',
    'neighbourhood',
    'quarter',
    'man_made',
    'locality',
    'locality_type',
    'postcode',
    'road',
    'building',
    'house_number',
    'apartment_number',
    'address_comment',
    'address_confirmed',
    'ISO3166-2-lvl6',
    'ISO3166-2-lvl4',
  ].concat(keepOptional))

  out = Object.fromEntries(
    Object.entries(out).filter(([k]) => allowed.has(k)),
  )

  // Remove empty/null/blank
  out = stripEmpty(out)

  // Safety: if locality is missing but we have suburb/municipality, lift one up as locality
  if (!out.locality) {
    const fallbackLocality = cleanStr(input.suburb)
      || cleanStr(input.municipality)
      || cleanStr(input.town)
      || cleanStr(input.city)
    if (fallbackLocality) {
      out.locality = fallbackLocality
      if (!out.locality_type) {
        // guess from the source key we picked
        if (cleanStr(input.suburb)) out.locality_type = 'suburb'
        else if (cleanStr(input.municipality)) out.locality_type = 'municipality'
        else if (cleanStr(input.town)) out.locality_type = 'town'
        else out.locality_type = 'city'
      }
    }
  }

  return out
}
